import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  BusinessIcon,
  Card,
  Chip,
  ChevronRightIcon,
  Collapse,
  RemoveCircleOutlineIcon,
  Text,
  cn,
} from '@billda/design-system';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { Deduction } from '../../types/potDetails.types';

interface DeductionGroup {
  groupName: string;
  deductions: Deduction[];
  totalAmount: number;
}

function groupDeductionsBySite(deductions: Deduction[]): DeductionGroup[] {
  const map = new Map<string, Deduction[]>();
  for (const d of deductions) {
    const key = d.siteName ?? 'Unassigned';
    const list = map.get(key) ?? [];
    list.push(d);
    map.set(key, list);
  }
  return Array.from(map.entries()).map(([groupName, items]) => ({
    groupName,
    deductions: items,
    // Cancelled deductions stay visible (struck through) but never count towards totals
    totalAmount: items.reduce(
      (sum, d) => (d.status === 'Cancelled' ? sum : sum + d.amount),
      0
    ),
  }));
}

interface PotDeductionsCardProps {
  deductions: Deduction[];
  onDeductionClick?: (deductionId: string) => void;
  className?: string;
}

export function PotDeductionsCard({
  deductions,
  onDeductionClick,
  className,
}: PotDeductionsCardProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const groups = useMemo(() => groupDeductionsBySite(deductions), [deductions]);

  if (deductions.length === 0) return null;

  return (
    <Card data-testid="pot-deductions-card" className={cn('h-full', className)}>
      <Text variant="h6" className="px-4 pb-2 pt-4 sm:px-6 sm:pt-6">
        Deductions ({deductions.length})
      </Text>

      {groups.map(group => {
        const expanded = !collapsedGroups.has(group.groupName);
        return (
          <div key={group.groupName}>
            <button
              type="button"
              onClick={() => {
                toggleGroup(group.groupName);
              }}
              aria-expanded={expanded}
              className="flex w-full cursor-pointer items-center justify-start gap-2 bg-surface-raised px-4 py-2 text-left transition-colors hover:bg-surface-pressed sm:px-6"
            >
              <ChevronRightIcon
                aria-hidden="true"
                className={cn(
                  'size-4 text-content-secondary transition-transform duration-150',
                  expanded && 'rotate-90'
                )}
              />
              <BusinessIcon
                aria-hidden="true"
                className="size-4 shrink-0 text-content-secondary"
              />
              <Text variant="body2" className="font-semibold">
                {group.groupName}
              </Text>
              <Text variant="body2" color="secondary">
                {group.deductions.length}
              </Text>
              <Text
                variant="body2"
                className="ml-auto font-semibold text-content-error"
              >
                {formatCurrency(group.totalAmount)}
              </Text>
            </button>

            <Collapse in={expanded} unmountOnExit>
              {group.deductions.map(deduction => (
                <DeductionListItem
                  key={deduction.id}
                  deduction={deduction}
                  onClick={onDeductionClick}
                />
              ))}
            </Collapse>
          </div>
        );
      })}
    </Card>
  );
}

const DeductionListItem = React.memo(function DeductionListItem({
  deduction,
  onClick,
}: {
  deduction: Deduction;
  onClick?: (deductionId: string) => void;
}) {
  const refLabel = deduction.uniqueReference
    ? `${deduction.uniqueReference} - ${deduction.description}`
    : deduction.description;

  const subheading =
    [deduction.siteName, deduction.plotReference].filter(Boolean).join(' · ') ||
    null;

  const isCancelled = deduction.status === 'Cancelled';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(deduction.id)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.(deduction.id);
        }
      }}
      className="flex cursor-pointer items-center gap-3 border-b border-border-default px-4 py-3 hover:bg-surface-raised sm:px-6"
    >
      <Avatar size="sm" className="shrink-0 bg-transparent">
        <RemoveCircleOutlineIcon
          aria-hidden="true"
          className="size-5 text-content-error"
        />
      </Avatar>

      <div className="min-w-0 flex-1">
        <Text variant="body2" className="truncate font-semibold">
          {refLabel}
        </Text>
        {subheading && (
          <Text variant="caption" color="secondary" className="block truncate">
            {subheading}
          </Text>
        )}
      </div>

      {isCancelled && (
        <Chip label="Cancelled" size="sm" className="shrink-0 font-semibold" />
      )}

      <Text
        variant="body2"
        className={
          isCancelled
            ? 'shrink-0 whitespace-nowrap font-semibold text-content-disabled line-through'
            : 'shrink-0 whitespace-nowrap font-semibold text-content-error'
        }
      >
        {formatCurrency(deduction.amount)}
      </Text>
    </div>
  );
});
